import { useFormikContext } from "formik";
import React, { FC, Fragment, useMemo } from "react";
import { GovernanceConfigurationType } from "../../../common/enums/clubs-wizard.enum";

import {
  ICreateClubWizardForm,
  ICreateClubWizardProps,
} from "../../../common/interfaces/clubs-wizard.interface";
import CustomCheckbox from "../../CustomCheckbox/CustomCheckbox";
import WizardField from "../../WizardField/WizardField";
import WizardHeader from "../WizardHeader/WizardHeader";
import "./ClubRoles.scss";

const ClubRoles: FC<ICreateClubWizardProps> = ({ formFields }) => {
  const { setFieldValue, values } = useFormikContext<ICreateClubWizardForm>();

  const updateCuratorField = (item) => {
    if (item.name === formFields.curator.name) {
      setFieldValue(item.name, !values.curator);
    }
  };

  const isClubRoleBased = useMemo(() => {
    return values.governance === "role-based";
  }, [values.governance]);

  const wizardHeaderDescription = useMemo(() => {
    return isClubRoleBased
      ? "Choose the roles that will be available in your Venture Club. Note that the founder and member roles are mandatory. Here you can also define the voting power for each role."
      : "Choose the roles that will be available in your Venture Club. Note that the founder and member roles are mandatory. Voting rights for each role will be determined based on their contribution to the treasury.";
  }, [isClubRoleBased]);

  const founderDescription = useMemo(() => {
    return values.governance === GovernanceConfigurationType.ProjectFundraise
      ? "This is you, the creator of the Club. The Founder can invite Investors to the club, and also create Discussion and Transfer Funds proposals. The Founder can not deposit into Fundraise and therefore, cannot vote when the proposal is active."
      : "This is you, the creator of the Club. Only the Founder can invite and accept members. The Founder can also create and cancel proposals that they or the Managers create. Only the Founder can create and cancel Staking accounts.";
  }, [values.governance]);

  return (
    <div className="club-roles">
      <WizardHeader description={wizardHeaderDescription} />
      <div className="club-roles__list">
        <div
          className={`club-roles__item club-roles__item--curator ${
            values.owner && "club-roles__item--active"
          }`}
        >
          <div className="club-roles__item-header">
            <div className="club-roles__check-label">
              <CustomCheckbox
                checked={values.owner}
                onClickMethod={() => updateCuratorField(formFields.owner)}
              />
              <p className="club-roles__label">{formFields.owner.label}</p>
            </div>
            {isClubRoleBased && (
              <WizardField
                name={formFields.ownerVotingPower.name}
                placeholder="Enter number of votes"
                type="number"
              />
            )}
          </div>

          <p className="club-roles__description">{founderDescription}</p>
        </div>

        {values.governance !== GovernanceConfigurationType.ProjectFundraise ? (
          <Fragment>
            <div
              className={`club-roles__item club-roles__item--curator ${
                values.member && "club-roles__item--active"
              }`}
            >
              <div className="club-roles__item-header">
                <div className="club-roles__check-label">
                  <CustomCheckbox
                    checked={values.member}
                    onClickMethod={() => updateCuratorField(formFields.member)}
                  />
                  <p className="club-roles__label">{formFields.member.label}</p>
                </div>
                {isClubRoleBased && (
                  <WizardField
                    name={formFields.memberVotingPower.name}
                    placeholder="Enter number of votes"
                    type="number"
                  />
                )}
              </div>

              <p className="club-roles__description">
                All people in the club are Members. Members can deposit into
                Fundraise, vote on proposals and deposit into Staking accounts.
              </p>
            </div>
            <div
              className={`club-roles__item club-roles__item--curator ${
                values.curator && "club-roles__item--active"
              }`}
            >
              <div className="club-roles__item-header">
                <div className="club-roles__check-label">
                  <CustomCheckbox
                    checked={values.curator}
                    onClickMethod={() => updateCuratorField(formFields.curator)}
                  />
                  <p className="club-roles__label">
                    {formFields.curator.label}
                  </p>
                </div>
                {isClubRoleBased && (
                  <WizardField
                    name={formFields.curatorVotingPower.name}
                    placeholder="Enter number of votes"
                    type="number"
                  />
                )}
              </div>

              <p className="club-roles__description">
                Manager can create proposals and only cancel proposals that they
                create.
              </p>
            </div>
          </Fragment>
        ) : (
          <div
            className={`club-roles__item club-roles__item--curator ${
              values.owner && "club-roles__item--active"
            }`}
          >
            <div className="club-roles__item-header">
              <div className="club-roles__check-label">
                <CustomCheckbox
                  checked={values.investor}
                  onClickMethod={() => updateCuratorField(formFields.investor)}
                />
                <p className="club-roles__label">{formFields.investor.label}</p>
              </div>
            </div>
            <p className="club-roles__description">
              Club participants whom the Founder (you) invites. They can deposit
              into Fundraise and vote on proposals. Only investors can create
              Withdrawal proposals. Their voting decisions determine how the
              club’s finances will be managed after Fundraise is closed.
            </p>
          </div>
        )}
        <div className={`club-roles__item `}>
          <div className="club-roles__item-header">
            <div className="club-roles__check-label">
              <CustomCheckbox
                checked={values.council}
                onClickMethod={() => {}}
              />
              <p className="club-roles__label">Custom</p>
            </div>
            <p className="club-roles__coming-soon">Coming soon</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubRoles;
